/**
 * AdminActivityPage - Super Admin product activity summary (Option B).
 * Manual refresh only — no auto-polling.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { useTranslation } from '../../i18n/hooks';
import { PageContainer, ArbiWinHeader, ArbiWinFooter, MainContainer, Button } from '../../ui';
import { getActivitySummary } from '../services/adminActivityService';
import './AdminActivityPage.css';

const MAX_RANGE_DAYS = 30;

const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 6);
  return { from: toIsoDate(from), to: toIsoDate(to) };
};

const daysInclusive = (fromStr, toStr) => {
  const from = new Date(`${fromStr}T00:00:00`);
  const to = new Date(`${toStr}T00:00:00`);
  return Math.floor((to - from) / (24 * 60 * 60 * 1000)) + 1;
};

/**
 * AdminActivityPage
 * @returns {JSX.Element}
 */
const AdminActivityPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const initial = defaultRange();

  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(async () => {
    if (!from || !to) {
      setErrorKey('invalidRange');
      setErrorDetail(null);
      return;
    }

    if (from > to) {
      setErrorKey('fromAfterTo');
      setErrorDetail(null);
      return;
    }

    const rangeDays = daysInclusive(from, to);
    if (rangeDays > MAX_RANGE_DAYS) {
      setErrorKey('rangeTooLarge');
      setErrorDetail(null);
      return;
    }

    setLoading(true);
    setErrorKey(null);
    setErrorDetail(null);

    const { data, error } = await getActivitySummary({ from, to });
    setLoading(false);

    if (error) {
      setSummary(null);
      if (error === 'forbidden' || error === 'unauthorized' || error === 'rate_limited' || error === 'bad_request') {
        setErrorKey(error);
      } else {
        setErrorKey('unknown');
        setErrorDetail(typeof error === 'string' ? error : null);
      }
      return;
    }

    setSummary(data);
  }, [from, to]);

  useEffect(() => {
    loadSummary();
    // Initial load only — subsequent loads via Refresh button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/');
    }
  };

  const resolveErrorMessage = () => {
    if (!errorKey) {
      return null;
    }
    if (errorKey === 'forbidden') {
      return t('admin.activity.errors.forbidden');
    }
    if (errorKey === 'unauthorized') {
      return t('admin.activity.errors.unauthorized');
    }
    if (errorKey === 'rate_limited') {
      return t('admin.activity.errors.rateLimited');
    }
    if (errorKey === 'rangeTooLarge') {
      return t('admin.activity.errors.rangeTooLarge', { max: MAX_RANGE_DAYS });
    }
    if (errorKey === 'fromAfterTo') {
      return t('admin.activity.errors.fromAfterTo');
    }
    if (errorKey === 'invalidRange') {
      return t('admin.activity.errors.invalidRange');
    }
    if (errorKey === 'bad_request') {
      return errorDetail || t('admin.activity.errors.badRequest');
    }
    return errorDetail || t('admin.activity.errors.unknown');
  };

  return (
    <PageContainer>
      <ArbiWinHeader
        isAuthenticated={isAuthenticated}
        user={user}
        onLogin={() => navigate('/login')}
        onRegister={() => navigate('/register')}
        onLogout={handleLogout}
        onProfile={() => navigate('/profile')}
        onHomeClick={() => navigate('/')}
      />

      <MainContainer>
        <div className="admin-activity-page">
          <header className="admin-activity-page__header">
            <h1 className="admin-activity-page__title">{t('admin.activity.title')}</h1>
            <p className="admin-activity-page__subtitle">{t('admin.activity.subtitle')}</p>
          </header>

          <form
            className="admin-activity-page__filters"
            onSubmit={(event) => {
              event.preventDefault();
              loadSummary();
            }}
          >
            <label className="admin-activity-page__field">
              <span>{t('admin.activity.from')}</span>
              <input
                type="date"
                value={from}
                max={to}
                onChange={(event) => setFrom(event.target.value)}
              />
            </label>
            <label className="admin-activity-page__field">
              <span>{t('admin.activity.to')}</span>
              <input
                type="date"
                value={to}
                min={from}
                onChange={(event) => setTo(event.target.value)}
              />
            </label>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? t('admin.activity.loading') : t('admin.activity.refresh')}
            </Button>
          </form>

          <p className="admin-activity-page__hint">
            {t('admin.activity.rangeHint', { max: MAX_RANGE_DAYS })}
          </p>

          {errorKey && (
            <div className="admin-activity-page__error" role="alert">
              {resolveErrorMessage()}
            </div>
          )}

          {summary && (
            <>
              <section className="admin-activity-page__section">
                <h2 className="admin-activity-page__section-title">
                  {t('admin.activity.totalsTitle')}
                </h2>
                <div className="admin-activity-page__table-wrap">
                  <table className="admin-activity-page__table">
                    <thead>
                      <tr>
                        <th>{t('admin.activity.columns.action')}</th>
                        <th>{t('admin.activity.columns.count')}</th>
                        <th>{t('admin.activity.columns.uniqueUsers')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary.totals || []).length === 0 ? (
                        <tr>
                          <td colSpan={3}>{t('admin.activity.empty')}</td>
                        </tr>
                      ) : (
                        summary.totals.map((row) => (
                          <tr key={row.action}>
                            <td>{row.action}</td>
                            <td>{row.count}</td>
                            <td>{row.uniqueUsers}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="admin-activity-page__section">
                <h2 className="admin-activity-page__section-title">
                  {t('admin.activity.byDayTitle')}
                </h2>
                <div className="admin-activity-page__table-wrap">
                  <table className="admin-activity-page__table">
                    <thead>
                      <tr>
                        <th>{t('admin.activity.columns.date')}</th>
                        <th>{t('admin.activity.columns.action')}</th>
                        <th>{t('admin.activity.columns.count')}</th>
                        <th>{t('admin.activity.columns.uniqueUsers')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary.byDay || []).length === 0 ? (
                        <tr>
                          <td colSpan={4}>{t('admin.activity.empty')}</td>
                        </tr>
                      ) : (
                        summary.byDay.map((row) => (
                          <tr key={`${row.date}-${row.action}`}>
                            <td>{row.date}</td>
                            <td>{row.action}</td>
                            <td>{row.count}</td>
                            <td>{row.uniqueUsers}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </MainContainer>

      <ArbiWinFooter />
    </PageContainer>
  );
};

export default AdminActivityPage;
